"use client";

import { createContext, type JSX, useContext, useRef } from "react";
import { type StoreApi, useStore } from "zustand";
import {
  createReorderCartStore,
  type ReorderCartStore,
} from "../store/reorder-cart-store";

export const ReorderCartStoreContext =
  createContext<StoreApi<ReorderCartStore> | null>(null);

// Dummy store for fallback when context doesn't exist
const dummyStore = createReorderCartStore({
  storageKey: "__dummy_reorder_cart__",
});

type Props = {
  children: JSX.Element;
};

export const ReorderCartProvider = ({ children }: Props) => {
  const storeRef = useRef<StoreApi<ReorderCartStore>>(undefined);
  if (!storeRef.current) {
    storeRef.current = createReorderCartStore({
      storageKey: "reorder-cart",
    });
  }

  return (
    <ReorderCartStoreContext.Provider value={storeRef.current}>
      {children}
    </ReorderCartStoreContext.Provider>
  );
};

export const useReorderCartStore = <T,>(
  selector: (store: ReorderCartStore) => T,
): T => {
  const cartStoreContext = useContext(ReorderCartStoreContext);

  if (!cartStoreContext) {
    throw new Error(
      "useReorderCartStore must be use within ReorderCartProvider",
    );
  }

  return useStore(cartStoreContext, selector);
};

// Store API accessor for reading fresh state inside async callbacks, where a
// value selected via useReorderCartStore would be stale (closure over a past
// render).
export const useReorderCartStoreApi = (): StoreApi<ReorderCartStore> => {
  const cartStoreContext = useContext(ReorderCartStoreContext);

  if (!cartStoreContext) {
    throw new Error(
      "useReorderCartStoreApi must be use within ReorderCartProvider",
    );
  }

  return cartStoreContext;
};

export const useReorderCartStoreSafe = <T,>(
  selector: (store: ReorderCartStore) => T,
): T | null => {
  const cartStoreContext = useContext(ReorderCartStoreContext);
  const store = cartStoreContext || dummyStore;
  const result = useStore(store, selector);

  // Return null if context doesn't exist, otherwise return the result
  return cartStoreContext ? result : null;
};
